import RAGApiService from './ragApiService';

export interface TaskPollingOptions {
  timeout?: number;
  pollInterval?: number;
  onComplete?: (task: any) => void;
  onError?: (error: Error) => void;
  onTimeout?: (task: any) => void;
}

export interface TaskPollingResult {
  success: boolean;
  task?: any;
  error?: Error;
  timeout?: boolean;
  pollingInfo?: {
    waited_seconds: number;
    completed: boolean;
    timeout?: boolean;
  };
}

export class TaskPollingService {
  private apiService: RAGApiService;
  private activePolls: Map<string, AbortController> = new Map();

  constructor(apiService: RAGApiService) {
    this.apiService = apiService;
  }

  /**
   * Start long polling for a task
   */
  async pollTask(
    taskId: string, 
    options: TaskPollingOptions = {}
  ): Promise<TaskPollingResult> {
    const {
      timeout = 30,
      pollInterval = 2.0,
      onComplete,
      onError,
      onTimeout
    } = options;

    // Cancel any existing poll for this task
    this.cancelPoll(taskId);

    // Create abort controller for this poll
    const abortController = new AbortController();
    this.activePolls.set(taskId, abortController);

    try {
      console.log(`[TASK POLLING] Starting long poll for task ${taskId}`);
      
      // Add a small delay before starting polling to allow task to be created
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const result = await this.apiService.pollTaskStatus(taskId, timeout, pollInterval);
      
      // Check if poll was aborted
      if (abortController.signal.aborted) {
        console.log(`[TASK POLLING] Poll for task ${taskId} was aborted`);
        return {
          success: false,
          error: new Error('Polling was cancelled')
        };
      }

      // Handle different response statuses
      if (result.status === 'success') {
        console.log(`[TASK POLLING] Task ${taskId} completed successfully`);
        onComplete?.(result.task);
        return {
          success: true,
          task: result.task,
          pollingInfo: result.polling_info
        };
      } else if (result.status === 'timeout') {
        console.log(`[TASK POLLING] Task ${taskId} polling timed out`);
        onTimeout?.(result.task);
        return {
          success: false,
          task: result.task,
          timeout: true,
          pollingInfo: result.polling_info
        };
      } else {
        throw new Error(`Unexpected polling result status: ${result.status}`);
      }

    } catch (error) {
      console.error(`[TASK POLLING] Error polling task ${taskId}:`, error);
      const errorObj = error instanceof Error ? error : new Error(String(error));
      
      // If it's a 404 error, wait a bit and try to refresh context
      if (errorObj.message.includes('404') || errorObj.message.includes('not found')) {
        console.log(`[TASK POLLING] Task ${taskId} not found, waiting 2 seconds and refreshing context`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Don't call onError for 404, just refresh context
        return {
          success: false,
          error: new Error('Task not found, may have been completed')
        };
      }
      
      onError?.(errorObj);
      return {
        success: false,
        error: errorObj
      };
    } finally {
      // Clean up
      this.activePolls.delete(taskId);
    }
  }

  /**
   * Cancel an active poll for a specific task
   */
  cancelPoll(taskId: string): void {
    const controller = this.activePolls.get(taskId);
    if (controller) {
      controller.abort();
      this.activePolls.delete(taskId);
      console.log(`[TASK POLLING] Cancelled poll for task ${taskId}`);
    }
  }

  /**
   * Cancel all active polls
   */
  cancelAllPolls(): void {
    for (const [taskId, controller] of Array.from(this.activePolls.entries())) {
      controller.abort();
      console.log(`[TASK POLLING] Cancelled poll for task ${taskId}`);
    }
    this.activePolls.clear();
  }

  /**
   * Check if a task is currently being polled
   */
  isPolling(taskId: string): boolean {
    return this.activePolls.has(taskId);
  }

  /**
   * Get list of currently polling task IDs
   */
  getActivePollingTasks(): string[] {
    return Array.from(this.activePolls.keys());
  }
}

// Singleton instance
let taskPollingServiceInstance: TaskPollingService | null = null;

export function getTaskPollingService(apiService?: RAGApiService): TaskPollingService {
  if (!taskPollingServiceInstance && apiService) {
    taskPollingServiceInstance = new TaskPollingService(apiService);
  }
  if (!taskPollingServiceInstance) {
    throw new Error('TaskPollingService not initialized. Please provide apiService.');
  }
  return taskPollingServiceInstance;
}
